/**
 * One question's row in the authoring list: its block header, its collapsed summary and its full
 * editor. Extracted verbatim from the inline map in SurveyQuestionsPreview — no behaviour change.
 */

import React from 'react';
import { AlertCircle, Copy, Edit3, Eye, GripVertical, Info, Plus, Save, Trash2, X } from 'lucide-react';
import Button from '../../components/Button';
import { UI_TEXT } from '../../config/ui-text';
import ImageHotspotEditor, { ImageHotspotConfig } from '../../components/survey/ImageHotspotEditor';
import MediaUploadGrid, { getMediaTypeFromUrl } from '../../components/survey/MediaUploadGrid';
import MediaUploadIcon from '../../components/survey/MediaUploadIcon';
import GroupMediaUpload, { getGroupColor } from '../../components/survey/GroupMediaUpload';
import { getGroupLabel } from '../../utils/groupUtils';
import type { SurveyBlockDefinition, RapidFireConfig } from '../../utils/surveyApi';
import type { ChoiceAction, ParsedQuestion } from '../../components/survey/types';
import type { ValidationIssue } from '../../utils/surveyValidation';
import { RAPID_FIRE_ALLOWED_TYPES, RAPID_FIRE_DEFAULT_DURATION_SECONDS } from './rapidFire';
import { selectionCap } from './multiSelect';
import { MAXDIFF_MODE_BEST_WORST, MAXDIFF_MODE_MULTI_SET } from './bestWorst';
import QuestionText from '../../components/common/QuestionText';
import QuestionIssueBadge from './QuestionIssueBadge';
import { AiDynamicSectionSettings } from './AiDynamicSectionSettings';
import { AiDynamicSectionSummary } from './AiDynamicSectionSummary';
import { dynamicSectionConfigFromRow, effectiveRubricStatus, type AiDynamicSectionConfig } from './aiDynamicSection';
import { useRubricStatusPolling } from './useRubricStatusPolling';
import {
  CONDITIONAL_ACTION_TYPES,
  DEFAULT_RAPID_FIRE_CONFIG,
  DEFAULT_RUBRIC_POINT_TIER,
  DISPLAY_CONDITION_TYPES,
  MAX_DIFF_ITEM_MEDIA_LIMIT,
  MAX_DIFF_ITEM_THUMB_CLASS,
  DEFAULT_BEST_WORST_QUOTA,
  MAX_SELECTIONS_HELP,
  MAX_SELECTIONS_LABEL,
  MAX_SELECTIONS_NONE,
  MAX_SELECTIONS_PLACEHOLDER,
  MAXDIFF_BEST_WORST_HELP,
  MAXDIFF_BEST_WORST_MODE_LABEL,
  MAXDIFF_MULTI_SET_MODE_LABEL,
  MAXDIFF_N_BEST_LABEL,
  MAXDIFF_N_WORST_LABEL,
  MEDIA_SUPPORTED_TYPES,
  QUESTION_TYPES,
  RAPID_FIRE_MAX_DURATION_SECONDS,
  RAPID_FIRE_MIN_DURATION_SECONDS,
  RUBRIC_INFO_TEXT,
  RUBRIC_TIER_OPTIONS,
  bestWorstModeSummary,
  blockAccentClass,
  formatRandomizationPoolLabel,
  hasDuplicateMaxDiffClaims,
  hasLookalikeMaxDiffClaims,
  hasMaxDiffItemVideo,
  hasSurplusMaxDiffItemMedia,
  maxSelectableOptions,
  nextRandomizationPoolId,
  optionGroupMediaGaps,
  parseMaxSelections,
  randomizationPoolBadgeClass,
  shouldRandomizeMedia,
  shownMaxDiffItemMedia,
  storedSelectionCap,
  tierLabel,
} from './questionCardHelpers';

interface QuestionCardProps {
  question: ParsedQuestion;
  index: number;
  issuesByQuestion: Map<string, ValidationIssue[]> | undefined;
  readOnly: boolean;
  groupCount: number;
  surveyId: number | null;
  groupSurveyIds: number[];
  groupLabels: string[];
  questions: ParsedQuestion[];
  setQuestions: React.Dispatch<React.SetStateAction<ParsedQuestion[]>>;
  setRequestedSelectionCaps: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  blocks: SurveyBlockDefinition[];
  setBlocks: React.Dispatch<React.SetStateAction<SurveyBlockDefinition[]>>;
  editingQuestionId: string | null;
  setEditingQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
  originalQuestionBeforeEdit: ParsedQuestion | null;
  setOriginalQuestionBeforeEdit: React.Dispatch<React.SetStateAction<ParsedQuestion | null>>;
  editingBlockId: string | null;
  setEditingBlockId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditingBlockConditionId: React.Dispatch<React.SetStateAction<string | null>>;
  editingBlockConditionId: string | null;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
  previewPickerFor: string | null;
  setPreviewPickerFor: React.Dispatch<React.SetStateAction<string | null>>;
  draggedIndex: number | null;
  dropTargetIndex: number | null;
  setExpandedQuestionMedia: React.Dispatch<React.SetStateAction<string | null>>;
  expandedQuestionMedia: string | null;
  setExpandedOptionMedia: React.Dispatch<React.SetStateAction<{ questionId: string; optionIndex: number; } | null>>;
  expandedOptionMedia: { questionId: string; optionIndex: number; } | null;
  randomizationPoolsInSurvey: string[];
  focusQuestionForEdit: (questionId: string) => void;
  openPreview: (groupSurveyId: number, startFrom?: number) => void;
  renderVideoThumb: (url: string, fullUrl?: string) => React.ReactElement;
  handlePreviewClick: (questionId: string | null, questionIndex?: number) => void;
  updateQuestion: (questionId: string, updates: Partial<ParsedQuestion>) => void;
  editorSelectionCap: (question: ParsedQuestion) => number | "";
  boundedSelectionCapNote: (question: ParsedQuestion) => string | null;
  repairedSelectionCapNote: (question: ParsedQuestion) => string | null;
  updateQuestionOptions: (questionId: string, updates: Partial<ParsedQuestion>) => void;
  changeQuestionType: (questionId: string, nextType: string) => void;
  removeQuestion: (questionId: string) => void;
  insertQuestionAt: (index: number) => void;
  duplicateQuestion: (questionId: string) => void;
  getBlockLabel: (blockId: string) => string;
  duplicateBlock: (blockId: string) => void;
  updateBlockRandomizeWithin: (blockId: string, value: boolean) => void;
  updateBlockDisplayCondition: (blockId: string, displayCondition: SurveyBlockDefinition["display_condition"] | undefined) => void;
  updateBlockLabel: (blockId: string, newLabel: string) => void;
  updateBlockRapidFire: (blockId: string, patch: Partial<RapidFireConfig> | null) => void;
  addChoice: (questionId: string) => void;
  removeChoice: (questionId: string, choiceIndex: number) => void;
  updateChoice: (questionId: string, choiceIndex: number, value: string) => void;
  addQuestionMedia: (questionId: string, url: string) => void;
  removeQuestionMedia: (questionId: string, index: number) => void;
  replaceQuestionMedia: (questionId: string, index: number, url: string) => void;
  reorderQuestionMedia: (questionId: string, fromIndex: number, toIndex: number) => void;
  addOptionMedia: (questionId: string, optionIndex: number, url: string) => void;
  removeOptionMedia: (questionId: string, optionIndex: number, mediaIndex: number) => void;
  replaceOptionMedia: (questionId: string, optionIndex: number, mediaIndex: number, url: string) => void;
  reorderOptionMedia: (questionId: string, optionIndex: number, fromIndex: number, toIndex: number) => void;
  getOptionMediaUrls: (question: ParsedQuestion, optionIndex: number) => string[];
  getOptionGroupMediaUrls: (question: ParsedQuestion, optionIndex: number) => Record<string, string[]>;
  countGroupMedia: (groupMedia: Record<string, string[]>) => number;
  addOptionGroupMedia: (questionId: string, optionIndex: number, groupIndex: number, url: string) => void;
  removeOptionGroupMedia: (questionId: string, optionIndex: number, groupIndex: number, mediaIndex: number) => void;
  replaceOptionGroupMedia: (questionId: string, optionIndex: number, groupIndex: number, mediaIndex: number, url: string) => void;
  updateChoiceAction: (questionId: string, choiceIndex: number, action?: "terminate" | "skip_to", target?: string, reason?: string) => void;
  getChoiceAction: (question: ParsedQuestion, choiceIndex: number) => ChoiceAction | undefined;
  addMatrixItem: (questionId: string) => void;
  removeMatrixItem: (questionId: string, itemIndex: number) => void;
  updateMatrixItem: (questionId: string, itemIndex: number, value: string) => void;
  addConjointAttribute: (questionId: string) => void;
  removeConjointAttribute: (questionId: string, attrIndex: number) => void;
  updateConjointAttribute: (questionId: string, attrIndex: number, field: string, value: unknown) => void;
  getQuestionTypeLabel: (type: string) => string;
  hasChoices: (type: string) => boolean;
  hasScale: (type: string) => boolean;
  hasRanking: (type: string) => boolean;
  hasMatrix: (type: string) => boolean;
  isMultiSelect: (type: string) => type is "multiple_choice";
  hasMaxDiff: (type: string) => boolean;
  hasConjoint: (type: string) => boolean;
  hasImageHotspot: (type: string) => boolean;
  hasAllocation: (type: string) => boolean;
  hasPhotoUpload: (type: string) => boolean;
  redirectConfigInvalid: boolean;
  commitQuestionsToParent: () => void;
  clearPendingEditState: () => void;
  handleDragStart: (e: React.DragEvent, index: number) => void;
  handleDragEnd: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent, index: number) => void;
  handleDragLeave: () => void;
  handleDrop: (e: React.DragEvent, dropIndex: number) => void;
  /** Regenerates one `ai_dynamic_section` question's rubric — surfaced by both
   *  `AiDynamicSectionSettings` (editing) and `AiDynamicSectionSummary` (not editing) when the
   *  backend marks it `rubric_stale`, via the same shared `AiDynamicSectionRubricStaleNotice`.
   *  This card does no fetching itself; omit when the caller has no way to perform the call, and
   *  neither renders regenerate/keep actions. */
  onRegenerateRubric?: (questionId: string) => Promise<void>;
  /** Tells the caller the researcher chose to keep the current (possibly stale) rubric as is. */
  onKeepRubric?: (questionId: string) => void;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  index,
  issuesByQuestion,
  readOnly,
  groupCount,
  surveyId,
  groupSurveyIds,
  groupLabels,
  questions,
  setQuestions,
  setRequestedSelectionCaps,
  blocks,
  setBlocks,
  editingQuestionId,
  setEditingQuestionId,
  originalQuestionBeforeEdit,
  setOriginalQuestionBeforeEdit,
  editingBlockId,
  setEditingBlockId,
  setEditingBlockConditionId,
  editingBlockConditionId,
  setHasChanges,
  previewPickerFor,
  setPreviewPickerFor,
  draggedIndex,
  dropTargetIndex,
  setExpandedQuestionMedia,
  expandedQuestionMedia,
  setExpandedOptionMedia,
  expandedOptionMedia,
  randomizationPoolsInSurvey,
  focusQuestionForEdit,
  openPreview,
  renderVideoThumb,
  handlePreviewClick,
  updateQuestion,
  editorSelectionCap,
  boundedSelectionCapNote,
  repairedSelectionCapNote,
  updateQuestionOptions,
  changeQuestionType,
  removeQuestion,
  insertQuestionAt,
  duplicateQuestion,
  getBlockLabel,
  duplicateBlock,
  updateBlockRandomizeWithin,
  updateBlockDisplayCondition,
  updateBlockLabel,
  updateBlockRapidFire,
  addChoice,
  removeChoice,
  updateChoice,
  addQuestionMedia,
  removeQuestionMedia,
  replaceQuestionMedia,
  reorderQuestionMedia,
  addOptionMedia,
  removeOptionMedia,
  replaceOptionMedia,
  reorderOptionMedia,
  getOptionMediaUrls,
  getOptionGroupMediaUrls,
  countGroupMedia,
  addOptionGroupMedia,
  removeOptionGroupMedia,
  replaceOptionGroupMedia,
  updateChoiceAction,
  getChoiceAction,
  addMatrixItem,
  removeMatrixItem,
  updateMatrixItem,
  addConjointAttribute,
  removeConjointAttribute,
  updateConjointAttribute,
  getQuestionTypeLabel,
  hasChoices,
  hasScale,
  hasRanking,
  hasMatrix,
  isMultiSelect,
  hasMaxDiff,
  hasConjoint,
  hasImageHotspot,
  hasAllocation,
  hasPhotoUpload,
  redirectConfigInvalid,
  commitQuestionsToParent,
  clearPendingEditState,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  onRegenerateRubric,
  onKeepRubric,
}) => {
  // Only ever runs for an `ai_dynamic_section` row whose rubric is pending — `isPending` gates the
  // hook's own internals, so this call stays unconditional (React hooks rule) while doing nothing
  // for every other question type. Resolution merges straight into `questions`, bypassing
  // `updateQuestion` deliberately: this is the backend catching up with what it already persisted,
  // not an unsaved local edit, and must not raise the "you have unsaved changes" banner.
  const dynamicSectionConfig = question.type === 'ai_dynamic_section' ? dynamicSectionConfigFromRow(question) : null;
  const rubricIsPending = dynamicSectionConfig !== null
    && effectiveRubricStatus(dynamicSectionConfig.rubric_status) === 'pending';
  const { timedOut: rubricPollTimedOut } = useRubricStatusPolling({
    surveyId,
    questionId: question.id,
    isPending: rubricIsPending,
    onResolved: (update) => {
      setQuestions((prev) => prev.map((q) => (
        q.id === question.id ? { ...q, ...(update as unknown as Partial<ParsedQuestion>) } : q
      )));
    },
  });

  return (
            <React.Fragment>
              {/* Insert Question Button - appears on hover between questions */}
              {!readOnly && editingQuestionId === null && (
                <div className="group relative h-4 -my-1">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex-1 h-px bg-gray-300" />
                    <button
                      onClick={() => insertQuestionAt(index)}
                      className="mx-2 flex items-center gap-1 px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm transition-all hover:shadow-md"
                    >
                      <Plus className="w-3 h-3" />
                      Insert Question
                    </button>
                    <div className="flex-1 h-px bg-gray-300" />
                  </div>
                </div>
              )}

              {(() => {
                const bid = question.block_id ?? null;
                const prevBid = index > 0 ? questions[index - 1].block_id ?? null : null;
                const showHeader = Boolean(bid && bid !== prevBid);
                if (!showHeader || !bid) return null;
                const bmeta = blocks.find((b) => b.id === bid);
                return (
                  <>
                  <div className="flex flex-wrap items-center gap-3 py-2 px-3 bg-slate-100 rounded-lg border border-slate-200 mb-2">
                    {editingBlockId === bid ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={bmeta?.label ?? ''}
                          onChange={(e) => updateBlockLabel(bid, e.target.value)}
                          className="text-sm font-semibold text-slate-800 px-2 py-1 border border-slate-300 rounded-md w-40"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              setEditingBlockId(null);
                            } else if (e.key === 'Escape') {
                              setEditingBlockId(null);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setEditingBlockId(null)}
                          className="text-xs text-slate-600 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-200"
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => !readOnly && setEditingBlockId(bid)}
                        className={`text-sm font-semibold text-slate-800 flex items-center gap-1.5 ${!readOnly ? 'hover:text-slate-600 cursor-pointer' : ''}`}
                        disabled={readOnly}
                      >
                        {getBlockLabel(bid)}
                        {!readOnly && <Edit3 className="w-3 h-3 text-slate-400" />}
                      </button>
                    )}
                    {!readOnly && (
                      <>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={bmeta?.randomize_within ?? false}
                            onChange={(e) => updateBlockRandomizeWithin(bid, e.target.checked)}
                          />
                          Randomize within block
                        </label>
                        <Button type="button" variant="outline" size="sm" onClick={() => duplicateBlock(bid)}>
                          Duplicate Block
                        </Button>
                        <Button
                          type="button"
                          variant={bmeta?.display_condition ? 'outline' : 'ghost'}
                          size="sm"
                          onClick={() => setEditingBlockConditionId(editingBlockConditionId === bid ? null : bid)}
                          className={bmeta?.display_condition ? 'border-amber-300 text-amber-700 hover:bg-amber-50' : ''}
                        >
                          {bmeta?.display_condition ? 'Edit Condition' : 'Add Condition'}
                        </Button>
                        <Button
                          type="button"
                          variant={bmeta?.rapid_fire?.enabled ? 'outline' : 'ghost'}
                          size="sm"
                          onClick={() => updateBlockRapidFire(
                            bid, bmeta?.rapid_fire?.enabled ? null : DEFAULT_RAPID_FIRE_CONFIG)}
                          className={bmeta?.rapid_fire?.enabled ? 'border-blue-500 text-blue-900 hover:bg-blue-50' : ''}
                        >
                          ⚡ Rapid-fire: {bmeta?.rapid_fire?.enabled ? 'On' : 'Off'}
                        </Button>
                      </>
                    )}
                    {readOnly && bmeta?.rapid_fire?.enabled && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-900 border border-blue-500">
                        ⚡ Rapid-fire · {bmeta.rapid_fire.duration_seconds}s
                      </span>
                    )}
                    {readOnly && bmeta?.randomize_within ? (
                      <span className="text-xs text-slate-600">Shuffles question order within this block</span>
                    ) : null}
                    {readOnly && bmeta?.display_condition && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Conditional
                      </span>
                    )}
                  </div>
                  {/* Rapid-fire settings */}
                  {!readOnly && bmeta?.rapid_fire?.enabled && (() => {
                    const rf = bmeta.rapid_fire!;
                    const poolQuestions = questions.filter((q) => q.block_id === bid);
                    const disallowed = poolQuestions.filter(
                      (q) => !RAPID_FIRE_ALLOWED_TYPES.has(q.type));
                    return (
                      <div className="border border-blue-500 bg-blue-50 rounded-lg p-4 mb-3">
                        <h4 className="text-xs font-bold text-blue-900 mb-1 uppercase tracking-wide">
                          ⚡ Rapid-fire section
                        </h4>
                        <p className="text-xs text-slate-600 mb-3.5 leading-relaxed">
                          Respondents answer as many of this block&apos;s questions as they can before
                          the clock runs out. Unanswered questions are recorded as not reached — never
                          as a failure.
                        </p>

                        <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1"
                                   htmlFor={`rf-duration-${bid}`}>
                              Duration (seconds)
                            </label>
                            <input
                              id={`rf-duration-${bid}`}
                              type="number"
                              min={RAPID_FIRE_MIN_DURATION_SECONDS}
                              max={RAPID_FIRE_MAX_DURATION_SECONDS}
                              value={rf.duration_seconds}
                              onChange={(e) => updateBlockRapidFire(bid, {
                                duration_seconds: parseInt(e.target.value, 10)
                                  || RAPID_FIRE_DEFAULT_DURATION_SECONDS,
                              })}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                              Default {RAPID_FIRE_DEFAULT_DURATION_SECONDS}. Between{' '}
                              {RAPID_FIRE_MIN_DURATION_SECONDS} and {RAPID_FIRE_MAX_DURATION_SECONDS}.
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1"
                                   htmlFor={`rf-max-${bid}`}>
                              Max questions served
                            </label>
                            <input
                              id={`rf-max-${bid}`}
                              type="number"
                              min={1}
                              placeholder={`All ${poolQuestions.length}`}
                              value={rf.max_questions ?? ''}
                              onChange={(e) => updateBlockRapidFire(bid, {
                                max_questions: e.target.value === ''
                                  ? null : parseInt(e.target.value, 10),
                              })}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                              Blank = the whole block. Lower than the block size gives each
                              respondent a random subset.
                            </p>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1"
                                   htmlFor={`rf-skip-${bid}`}>
                              Respondents can skip
                            </label>
                            <select
                              id={`rf-skip-${bid}`}
                              value={rf.allow_skip ? 'yes' : 'no'}
                              onChange={(e) => updateBlockRapidFire(
                                bid, { allow_skip: e.target.value === 'yes' })}
                              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                            >
                              <option value="no">No — answer or wait out the clock</option>
                              <option value="yes">Yes — show a Skip button</option>
                            </select>
                            <p className="text-[11px] text-gray-500 mt-1">
                              Default off. Skips are recorded, so you can see the rate.
                            </p>
                          </div>

                          <div>
                            {/* Read-only mirror, not a second control: order randomization IS the
                                "Randomize within block" checkbox above. Two controls meaning the
                                same thing could disagree. */}
                            <span className="block text-xs font-semibold text-gray-700 mb-1">
                              Question order
                            </span>
                            <div className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-600">
                              {bmeta?.randomize_within
                                ? 'Randomized per respondent' : 'Same order for everyone'}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">
                              Follows “Randomize within block” above.
                            </p>
                          </div>
                        </div>

                        <div className="mt-3.5">
                          <label className="block text-xs font-semibold text-gray-700 mb-1"
                                 htmlFor={`rf-intro-${bid}`}>
                            Intro text
                          </label>
                          <input
                            id={`rf-intro-${bid}`}
                            type="text"
                            value={rf.intro_text ?? ''}
                            placeholder={`You'll have ${rf.duration_seconds} seconds to answer as many questions as you can. Answer quickly — there are no right or wrong answers.`}
                            onChange={(e) => updateBlockRapidFire(
                              bid, { intro_text: e.target.value || undefined })}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                          />
                        </div>

                        <div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-amber-800 leading-relaxed mt-3">
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>Only one-tap questions and numeric input can go in a rapid-fire
                            block.</strong>{' '}
                            Single choice, Yes/No, rating, Likert, NPS, dropdown and numeric input
                            are allowed — everything else can&apos;t be answered in a few seconds.
                            AI follow-up is switched off inside this block.
                            {disallowed.length > 0 && (
                              <span className="block mt-1.5 font-semibold text-red-700">
                                {disallowed.length} question{disallowed.length === 1 ? '' : 's'} in
                                this block {disallowed.length === 1 ? 'is' : 'are'} not allowed:{' '}
                                {disallowed.map((q) => q.type).join(', ')}. Move{' '}
                                {disallowed.length === 1 ? 'it' : 'them'} out of the block or change
                                the type before saving.
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Block Condition Editor */}
                  {!readOnly && editingBlockConditionId === bid && (() => {
                    // Find index of first question in this block to determine eligible questions
                    const firstQInBlockIdx = questions.findIndex(q => q.block_id === bid);
                    const eligibleQuestions = firstQInBlockIdx > 0
                      ? questions.slice(0, firstQInBlockIdx).filter(q => DISPLAY_CONDITION_TYPES.includes(q.type))
                      : [];

                    const dc = bmeta?.display_condition;
                    const conditions = dc?.conditions || [];
                    const logic = dc?.logic || 'and';

                    const updateBlockConditions = (newConditions: any[], newLogic?: string) => {
                      if (newConditions.length === 0) {
                        updateBlockDisplayCondition(bid, undefined);
                      } else {
                        updateBlockDisplayCondition(bid, { logic: (newLogic || logic) as 'and' | 'or', conditions: newConditions });
                      }
                    };

                    return (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-amber-800">Block Display Condition</span>
                          <button
                            type="button"
                            onClick={() => setEditingBlockConditionId(null)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {eligibleQuestions.length === 0 ? (
                          <p className="text-sm text-amber-700">
                            No eligible questions before this block. Add choice or percentage allocation questions before the block to enable conditions.
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {conditions.length > 1 && (
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-amber-700">Logic:</span>
                                <select
                                  value={logic}
                                  onChange={(e) => updateBlockConditions(conditions, e.target.value)}
                                  className="text-xs border border-amber-300 rounded px-2 py-1"
                                >
                                  <option value="and">AND (all must match)</option>
                                  <option value="or">OR (any must match)</option>
                                </select>
                              </div>
                            )}

                            {conditions.map((cond: any, condIdx: number) => {
                              const sourceQ = questions.find(q => q.id === cond.source_question_id);
                              const isPct = sourceQ?.type === 'percentage_allocation';
                              return (
                                <div key={condIdx} className="bg-white border border-amber-200 rounded p-2 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-amber-600">Condition {condIdx + 1}</span>
                                    <button
                                      type="button"
                                      onClick={() => updateBlockConditions(conditions.filter((_, i) => i !== condIdx))}
                                      className="text-amber-500 hover:text-amber-700"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <select
                                      value={cond.source_question_id}
                                      onChange={(e) => {
                                        const newSrc = questions.find(q => q.id === e.target.value);
                                        const newIsPct = newSrc?.type === 'percentage_allocation';
                                        const updated = newIsPct
                                          ? { source_question_id: e.target.value, operator: '>', option_key: newSrc?.allocation_options?.[0] || newSrc?.choices?.[0] || '', threshold: 0, value: '' }
                                          : { source_question_id: e.target.value, operator: 'selected', value: newSrc?.choices?.[0] || '' };
                                        updateBlockConditions(conditions.map((c, i) => i === condIdx ? updated : c));
                                      }}
                                      className="text-xs border border-gray-300 rounded px-2 py-1"
                                    >
                                      {eligibleQuestions.map((eq) => (
                                        <option key={eq.id} value={eq.id}>
                                          Q{questions.indexOf(eq) + 1}: {eq.text.slice(0, 30)}...
                                        </option>
                                      ))}
                                    </select>
                                    {isPct ? (
                                      <>
                                        <select
                                          value={cond.option_key || ''}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, option_key: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {(sourceQ?.allocation_options || sourceQ?.choices || []).map((opt: string) => (
                                            <option key={opt} value={opt}>{opt}</option>
                                          ))}
                                        </select>
                                        <select
                                          value={cond.operator}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, operator: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          <option value=">">&gt;</option>
                                          <option value=">=">&ge;</option>
                                          <option value="=">=</option>
                                          <option value="<">&lt;</option>
                                          <option value="<=">&le;</option>
                                        </select>
                                        <input
                                          type="number"
                                          value={cond.threshold ?? 0}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, threshold: parseInt(e.target.value) || 0 } : c))}
                                          className="w-16 text-xs border border-gray-300 rounded px-2 py-1"
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <select
                                          value={cond.operator}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, operator: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          <option value="selected">selected</option>
                                          <option value="not_selected">not selected</option>
                                        </select>
                                        <select
                                          value={cond.value}
                                          onChange={(e) => updateBlockConditions(conditions.map((c, i) => i === condIdx ? { ...c, value: e.target.value } : c))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          {(sourceQ?.choices || []).map((ch: string) => (
                                            <option key={ch} value={ch}>{ch}</option>
                                          ))}
                                        </select>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            <button
                              type="button"
                              onClick={() => {
                                const sq = eligibleQuestions[eligibleQuestions.length - 1];
                                const isPct = sq.type === 'percentage_allocation';
                                const newCond = isPct
                                  ? { source_question_id: sq.id, operator: '>', option_key: sq.allocation_options?.[0] || sq.choices?.[0] || '', threshold: 0, value: '' }
                                  : { source_question_id: sq.id, operator: 'selected', value: sq.choices?.[0] || '' };
                                updateBlockConditions([...conditions, newCond]);
                              }}
                              className="text-xs text-amber-700 hover:text-amber-900"
                            >
                              + Add condition
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Block Condition Summary - show when condition exists but not editing */}
                  {!readOnly && editingBlockConditionId !== bid && bmeta?.display_condition && (() => {
                    const dc = bmeta.display_condition;
                    const conditions = dc.conditions || [];
                    const logic = dc.logic || 'and';

                    const summaries = conditions.map((cond: any) => {
                      const sourceQ = questions.find(q => q.id === cond.source_question_id);
                      const sourceIdx = sourceQ ? questions.indexOf(sourceQ) + 1 : '?';
                      if (sourceQ?.type === 'percentage_allocation') {
                        const opDisplay: Record<string, string> = { '>': '>', '>=': '\u2265', '=': '=', '<': '<', '<=': '\u2264' };
                        return `Q${sourceIdx} "${cond.option_key}" ${opDisplay[cond.operator] || cond.operator} ${cond.threshold}`;
                      }
                      return `Q${sourceIdx} "${cond.value}" ${cond.operator === 'selected' ? 'selected' : 'not selected'}`;
                    });

                    return (
                      <div className="mt-1 text-xs text-amber-700">
                        Show block when: {summaries.join(` ${logic.toUpperCase()} `)}
                      </div>
                    );
                  })()}
                  </>
                );
              })()}

              <div
                id={`question-${question.id}`}
                draggable={!editingQuestionId && !readOnly}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={`border rounded-lg p-4 transition-colors ${blockAccentClass(question.block_id)} ${
                  draggedIndex === index
                    ? 'border-blue-500 bg-blue-50 opacity-50'
                    : dropTargetIndex === index
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
            {/* Question Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-2 flex-wrap gap-y-1">
                  <span className="text-sm font-medium text-gray-500 mr-3">
                    Q{index + 1}
                  </span>
                  {issuesByQuestion && (
                    <QuestionIssueBadge
                      issues={issuesByQuestion.get(question.id) ?? []}
                      onClick={() => focusQuestionForEdit(question.id)}
                    />
                  )}
                  {question.randomization_group && (
                    <span
                      className={`mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${randomizationPoolBadgeClass(
                        question.randomization_group,
                        randomizationPoolsInSurvey
                      )}`}
                      title={`Randomization pool: ${question.randomization_group}`}
                    >
                      {formatRandomizationPoolLabel(question.randomization_group)}
                    </span>
                  )}
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getQuestionTypeLabel(question.type)}
                  </span>
                  {question.block_id ? (
                    <span className="mr-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 text-slate-800 border border-slate-300">
                      {getBlockLabel(question.block_id)}
                    </span>
                  ) : null}
                  {groupCount > 1 && editingQuestionId !== question.id && (
                    <div className="ml-2 flex items-center gap-1">
                      {Array.from({ length: groupCount }, (_, gi) => {
                        const active = !question.visible_groups || question.visible_groups.includes(gi);
                        const color = getGroupColor(gi);
                        return (
                          <span
                            key={gi}
                            title={`${getGroupLabel(gi)}${active ? '' : ' (hidden)'}`}
                            className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold ${
                              active
                                ? `${color.header} text-white`
                                : 'bg-gray-200 text-gray-400'
                            }`}
                          >
                            {String.fromCharCode(65 + gi)}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {editingQuestionId === question.id ? (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      {question.type === 'ai_dynamic_section' ? (
                        // This type writes its own questions each turn from the Goal and rubric
                        // below; SurveySession delegates rendering to AiDynamicSectionRunner and
                        // never reads question.text. Showing the box would invite an author to
                        // write something that is silently discarded.
                        <p className="flex-1 text-sm text-gray-500 italic px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                          This section writes its own questions as the conversation goes. Set what it
                          should find out under Goal and Topics below.
                        </p>
                      ) : (
                        <textarea
                          value={question.text}
                          onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md resize-y min-h-[60px]"
                          rows={3}
                          placeholder={UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.QUESTION_TEXT_PLACEHOLDER}
                        />
                      )}
                      {/* Question-level Media Upload Icon */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && (() => {
                        const groupMediaCount = question.group_media ? Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0) : 0;
                        const totalMedia = (question.media_urls?.length || 0) + groupMediaCount;
                        return (
                          <MediaUploadIcon
                            hasMedia={totalMedia > 0}
                            mediaCount={totalMedia}
                            onClick={() => setExpandedQuestionMedia(
                              expandedQuestionMedia === question.id ? null : question.id
                            )}
                          />
                        );
                      })()}
                    </div>

                    {/* Description field for info_screen questions */}
                    {question.type === 'info_screen' && (
                      <div className="mt-3">
                        <label className="block text-xs text-gray-600 mb-1">
                          Additional Description (shown as gray text below main content)
                        </label>
                        <textarea
                          value={question.description || ''}
                          onChange={(e) => updateQuestion(question.id, { description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                          rows={3}
                          placeholder="Optional additional context shown to respondents as gray text"
                        />
                      </div>
                    )}

                    {/* Question-level Media Grid - show if has media OR is expanded */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && 
                     ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0) || expandedQuestionMedia === question.id) && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-xs font-medium text-gray-600 mb-2">Question Media</div>
                        {groupCount > 1 ? (
                          <GroupMediaUpload
                            groupCount={groupCount}
                            visibleGroups={question.visible_groups}
                            groupMedia={question.group_media || {}}
                            onMediaAdd={(gi, url) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                gm[String(gi)] = [...(gm[String(gi)] || []), url];
                                return { ...q, group_media: gm };
                              }));
                              setHasChanges(true);
                            }}
                            onMediaRemove={(gi, removeIdx) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                const arr = (gm[String(gi)] || []).filter((_, i) => i !== removeIdx);
                                if (arr.length > 0) gm[String(gi)] = arr;
                                else delete gm[String(gi)];
                                return { ...q, group_media: Object.keys(gm).length > 0 ? gm : undefined };
                              }));
                              setHasChanges(true);
                            }}
                            onMediaReplace={(gi, mediaIdx, newUrl) => {
                              setQuestions(prev => prev.map(q => {
                                if (q.id !== question.id) return q;
                                const gm = { ...(q.group_media || {}) };
                                const arr = [...(gm[String(gi)] || [])];
                                if (mediaIdx < 0 || mediaIdx >= arr.length) return q;
                                arr[mediaIdx] = newUrl;
                                gm[String(gi)] = arr;
                                return { ...q, group_media: gm };
                              }));
                              setHasChanges(true);
                            }}
                            maxItemsPerGroup={5}
                          />
                        ) : (
                          <MediaUploadGrid
                            mediaUrls={question.media_urls || []}
                            onMediaAdd={(url) => addQuestionMedia(question.id, url)}
                            onMediaRemove={(index) => removeQuestionMedia(question.id, index)}
                            onMediaReplace={(index, url) => replaceQuestionMedia(question.id, index, url)}
                            onMediaReorder={(from, to) => reorderQuestionMedia(question.id, from, to)}
                            maxItems={5}
                          />
                        )}
                        {groupCount === 1 && (question.media_urls?.length || 0) >= 2 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={shouldRandomizeMedia(question)}
                                onChange={(e) => updateQuestion(question.id, {
                                  randomize_media_order: e.target.checked,
                                  image_position_mode: null,
                                  image_order: null,
                                })}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <span className="text-xs font-medium text-gray-600">
                                Randomize image/video order per respondent
                              </span>
                            </label>
                            {shouldRandomizeMedia(question) && (
                              <p className="text-xs text-gray-500 mt-1 ml-6">
                                Each respondent will see media in a different random order. Drag-and-drop above to set the default order.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <select
                      value={question.type}
                      onChange={(e) => changeQuestionType(question.id, e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-500">Block</span>
                      <select
                        value={question.block_id ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '__new__') {
                            const id = `block_${Date.now()}`;
                            const label = `Block ${blocks.length + 1}`;
                            setBlocks([...blocks, { id, label, randomize_within: false }]);
                            updateQuestion(question.id, { block_id: id });
                            setHasChanges(true);
                          } else {
                            updateQuestion(question.id, { block_id: val === '' ? null : val });
                          }
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm min-w-[10rem]"
                      >
                        <option value="">None</option>
                        {blocks.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.label}
                          </option>
                        ))}
                        <option value="__new__">+ New Block</option>
                      </select>
                    </div>

                    {/* Visible to Groups — compact toggle row */}
                    {groupCount > 1 && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-gray-500 mr-1">Groups:</span>
                        {Array.from({ length: groupCount }, (_, gi) => {
                          const selected = !question.visible_groups || question.visible_groups.includes(gi);
                          const color = getGroupColor(gi);
                          return (
                            <button
                              key={gi}
                              type="button"
                              onClick={() => {
                                const current = question.visible_groups || Array.from({ length: groupCount }, (_, i) => i);
                                const next = selected
                                  ? current.filter(g => g !== gi)
                                  : [...current, gi].sort();
                                updateQuestion(question.id, {
                                  visible_groups: next.length === groupCount ? undefined : next,
                                });
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-all ${
                                selected
                                  ? `${color.header} text-white shadow-sm`
                                  : 'bg-gray-100 text-gray-400 line-through'
                              }`}
                            >
                              {getGroupLabel(gi)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2">
                      <QuestionText className="text-gray-900 font-medium flex-1">
                        {question.type === 'ai_dynamic_section'
                          // Read mode: question.text is empty for this type, so name what the
                          // section is rather than showing a blank line. The goal is NOT read
                          // here — it is not on ParsedQuestion, and reaching past the type to
                          // fetch it is how this file accumulates casts.
                          ? 'Adaptive section — writes its own questions'
                          : question.text}
                      </QuestionText>
                      {/* Show media count badge in read mode */}
                      {MEDIA_SUPPORTED_TYPES.includes(question.type) && ((question.media_urls?.length || 0) > 0 || (question.group_media && Object.keys(question.group_media).length > 0)) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {question.group_media
                            ? `${Object.values(question.group_media).reduce((s, arr) => s + arr.length, 0)} media`
                            : `${question.media_urls?.length} media`
                          }
                        </span>
                      )}
                    </div>
                    {/* Show description for info_screen in read mode */}
                    {question.type === 'info_screen' && question.description && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-gray-500 text-sm leading-relaxed">
                          {question.description}
                        </p>
                      </div>
                    )}
                    {/* Show media previews in read mode */}
                    {MEDIA_SUPPORTED_TYPES.includes(question.type) && (question.media_urls?.length || 0) > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(question.media_urls || []).map((url, idx) => {
                          const fullUrl = url.startsWith('/api/')
                            ? `${import.meta.env.VITE_API_URL || ''}${url}`
                            : url;
                          const mediaType = getMediaTypeFromUrl(url);
                          return (
                            <div key={idx} className="w-20 h-20 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                              {mediaType === 'video' ? (
                                renderVideoThumb(url, fullUrl)
                              ) : (
                                <img
                                  src={fullUrl}
                                  alt={`Question media ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                  }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Show group media previews in read mode */}
                    {groupCount > 1 && question.group_media && Object.keys(question.group_media).length > 0 && (
                      <div className="space-y-2">
                        {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                          const urls = question.group_media?.[String(gi)];
                          if (!urls || urls.length === 0) return null;
                          const color = getGroupColor(gi);
                          return (
                            <div key={gi} className={`rounded-lg border ${color.border} ${color.bg} p-2`}>
                              <span className={`text-[10px] font-semibold uppercase ${color.text}`}>{getGroupLabel(gi)}</span>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {urls.map((url, idx) => {
                                  const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                  const mediaType = getMediaTypeFromUrl(url);
                                  return (
                                    <div key={idx} className="w-16 h-16 rounded border border-gray-200 overflow-hidden bg-white">
                                      {mediaType === 'video' ? (
                                        renderVideoThumb(url, fullUrl)
                                      ) : (
                                        <img
                                          src={fullUrl}
                                          alt={`${getGroupLabel(gi)} media ${idx + 1}`}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                          }}
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3 ml-4">
                {/* Randomization pool: none, existing pools, or new pool */}
                {!readOnly && (
                  <div className="group relative flex flex-col items-end gap-0.5">
                    <label className="sr-only" htmlFor={`randomization-pool-${question.id}`}>
                      Randomization pool
                    </label>
                    <select
                      id={`randomization-pool-${question.id}`}
                      value={question.randomization_group ?? ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        const v = e.target.value;
                        if (v === '__new__') {
                          const ids = questions
                            .map((q) => q.randomization_group)
                            .filter((g): g is string => Boolean(g));
                          updateQuestion(question.id, {
                            randomization_group: nextRandomizationPoolId(ids),
                          });
                        } else {
                          updateQuestion(question.id, {
                            randomization_group: v === '' ? null : v,
                          });
                        }
                      }}
                      className="max-w-[9.5rem] text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white text-gray-800 shadow-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      title="Randomization pool (questions shuffle only within the same pool)"
                    >
                      <option value="">None</option>
                      <option value="default">{formatRandomizationPoolLabel('default')}</option>
                      {randomizationPoolsInSurvey
                        .filter((pid) => pid !== 'default')
                        .map((pid) => (
                          <option key={pid} value={pid}>
                            {formatRandomizationPoolLabel(pid)}
                          </option>
                        ))}
                      <option value="__new__">New pool…</option>
                    </select>
                    <span className="absolute bottom-full right-0 mb-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-[14rem] text-right">
                      Shuffle with others in the same pool only
                    </span>
                  </div>
                )}
                {/* Preview from here button */}
                {(surveyId || groupSurveyIds.length > 0) && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewClick(question.id, index);
                      }}
                      className="group relative text-gray-400 hover:text-blue-600 transition-colors"
                      title="Preview from this question"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        Preview from here
                      </span>
                    </button>
                    {previewPickerFor === question.id && (
                      <>
                      <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setPreviewPickerFor(null); }} />
                      <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30 min-w-[160px]">
                        <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 border-b border-gray-100">Select group to preview</div>
                        {groupSurveyIds.map((sid, idx) => (
                          <button
                            key={sid}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openPreview(sid, index); }}
                            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700"
                          >
                            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${getGroupColor(idx).header}`} />
                            {groupLabels[idx] || `Group ${idx + 1}`}
                          </button>
                        ))}
                      </div>
                      </>
                    )}
                  </div>
                )}
                {/* Drag Handle - positioned on the right */}
                {!readOnly && !editingQuestionId && (
                  <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                )}
                {editingQuestionId === question.id ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        commitQuestionsToParent();
                        clearPendingEditState();
                      }}
                      icon={<Save className="w-4 h-4" />}
                      disabled={redirectConfigInvalid}
                    >
                      {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.SAVE}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Restore original question if we have it
                        if (originalQuestionBeforeEdit) {
                          const restoredQuestions = questions.map(q =>
                            q.id === originalQuestionBeforeEdit.id ? originalQuestionBeforeEdit : q
                          );
                          setQuestions(restoredQuestions);
                        }
                        clearPendingEditState();
                      }}
                      icon={<X className="w-4 h-4" />}
                    >
                      {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.CANCEL}
                    </Button>
                  </>
                ) : (
                  !readOnly && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }}
                        icon={<Edit3 className="w-4 h-4" />}
                      >
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.EDIT}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => duplicateQuestion(question.id)}
                        icon={<Copy className="w-4 h-4" />}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                        icon={<Trash2 className="w-4 h-4 text-red-600" />}
                        className="text-red-600 hover:text-red-700"
                      >
                        Delete
                      </Button>
                    </>
                  )
                )}
              </div>
            </div>

            {/* Question Details */}
            <div className="mt-3 space-y-3">
              {/* AI Dynamic Section config. ALWAYS visible (product-owner ruling: "all
                  configuration of question shall be visible even without edit mode") — editable
                  while editing this question, read-only summary otherwise. Never a disabled copy
                  of the edit form; the read view is its own component, laid out for reading. */}
              {question.type === 'ai_dynamic_section' && dynamicSectionConfig && (
                editingQuestionId === question.id ? (
                  <AiDynamicSectionSettings
                    config={dynamicSectionConfig}
                    onChange={(config: AiDynamicSectionConfig) =>
                      updateQuestion(question.id, config as unknown as Partial<ParsedQuestion>)
                    }
                    availableQuestions={questions.slice(0, index).map((q) => ({ id: q.id, text: q.text }))}
                    onRegenerateRubric={
                      onRegenerateRubric ? () => onRegenerateRubric(question.id) : undefined
                    }
                    onKeepRubric={onKeepRubric ? () => onKeepRubric(question.id) : undefined}
                    rubricPollTimedOut={rubricPollTimedOut}
                  />
                ) : (
                  <AiDynamicSectionSummary
                    config={dynamicSectionConfig}
                    availableQuestions={questions.slice(0, index).map((q) => ({ id: q.id, text: q.text }))}
                    rubricPollTimedOut={rubricPollTimedOut}
                    onRegenerateRubric={
                      onRegenerateRubric ? () => onRegenerateRubric(question.id) : undefined
                    }
                    onKeepRubric={onKeepRubric ? () => onKeepRubric(question.id) : undefined}
                  />
                )
              )}

              {/* Choices for choice-based questions */}
              {hasChoices(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.ANSWER_CHOICES}
                  </label>
                  <div className="space-y-3">
                    {(question.choices || []).map((choice, choiceIndex) => {
                      const supportsMedia = MEDIA_SUPPORTED_TYPES.includes(question.type);
                      const hasMediaContent = (question.media_urls && question.media_urls.length > 0)
                        || (question.option_media && Object.keys(question.option_media).length > 0)
                        || (question.group_media && Object.keys(question.group_media).length > 0)
                        || (question.option_group_media && Object.keys(question.option_group_media).length > 0);
                      const showMediaLayout = supportsMedia && (hasMediaContent || editingQuestionId === question.id);

                      return (
                        <div key={choiceIndex} className={`${showMediaLayout ? 'border border-gray-200 rounded-lg p-3' : 'flex items-center space-x-2'}`}>
                          {showMediaLayout ? (
                            // Media + Choice Layout
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-1">
                                  <span className="text-sm text-gray-500 w-8">
                                    {choiceIndex + 1}.
                                  </span>
                                  {editingQuestionId === question.id ? (
                                    <>
                                      <input
                                        type="text"
                                        value={choice}
                                        onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                        className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                      />
                                      {/* Option-level Media Upload Icon */}
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeChoice(question.id, choiceIndex)}
                                        icon={<X className="w-3 h-3" />}
                                      >
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{choice}</span>
                                      </div>
                                      {/* Option media preview in read mode */}
                                      {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                            const fullUrl = url.startsWith('/api/')
                                              ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                              : url;
                                            const mediaType = getMediaTypeFromUrl(url);
                                            return (
                                              <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                                {mediaType === 'video' ? (
                                                  renderVideoThumb(url, fullUrl)
                                                ) : (
                                                  <img
                                                    src={fullUrl}
                                                    alt={`${choice} media ${mediaIdx + 1}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                      const target = e.target as HTMLImageElement;
                                                      target.style.display = 'none';
                                                    }}
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {/* Option group media preview in read mode */}
                                      {groupCount > 1 && question.option_group_media?.[String(choiceIndex)] && Object.keys(question.option_group_media[String(choiceIndex)]).length > 0 && (
                                        <div className="mt-2 space-y-1.5">
                                          {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                                            const urls = question.option_group_media?.[String(choiceIndex)]?.[String(gi)];
                                            if (!urls || urls.length === 0) return null;
                                            const color = getGroupColor(gi);
                                            return (
                                              <div key={gi} className={`rounded border ${color.border} ${color.bg} px-2 py-1 flex items-center gap-2`}>
                                                <span className={`text-[9px] font-semibold uppercase ${color.text} shrink-0`}>{getGroupLabel(gi)}</span>
                                                <div className="flex flex-wrap gap-1">
                                                  {urls.map((url, idx) => {
                                                    const fullUrl = url.startsWith('/api/') ? `${import.meta.env.VITE_API_URL || ''}${url}` : url;
                                                    const mediaType = getMediaTypeFromUrl(url);
                                                    return (
                                                      <div key={idx} className="w-10 h-10 rounded border border-gray-200 overflow-hidden bg-white">
                                                        {mediaType === 'video' ? (
                                                          renderVideoThumb(url, fullUrl)
                                                        ) : (
                                                          <img
                                                            src={fullUrl}
                                                            alt={`${getGroupLabel(gi)} ${choice} ${idx + 1}`}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                              (e.target as HTMLImageElement).style.display = 'none';
                                                            }}
                                                          />
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Option-level Media Grid - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={getOptionGroupMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(gi, url) =>
                                        addOptionGroupMedia(question.id, choiceIndex, gi, url)
                                      }
                                      onMediaRemove={(gi, removeIdx) =>
                                        removeOptionGroupMedia(question.id, choiceIndex, gi, removeIdx)
                                      }
                                      onMediaReplace={(gi, mediaIdx, newUrl) =>
                                        replaceOptionGroupMedia(question.id, choiceIndex, gi, mediaIdx, newUrl)
                                      }
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      onMediaReplace={(index, url) =>
                                        replaceOptionMedia(question.id, choiceIndex, index, url)
                                      }
                                      onMediaReorder={(from, to) =>
                                        reorderOptionMedia(question.id, choiceIndex, from, to)
                                      }
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Media Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 mt-2 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            // Regular Choice Layout
                            <div className="flex flex-col space-y-2 w-full">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500 w-8">
                                  {choiceIndex + 1}.
                                </span>
                                {editingQuestionId === question.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={choice}
                                      onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    {/* Option-level Media Upload Icon for regular layout */}
                                    {supportsMedia && (
                                      <MediaUploadIcon
                                        hasMedia={getOptionMediaUrls(question, choiceIndex).length > 0}
                                        mediaCount={getOptionMediaUrls(question, choiceIndex).length}
                                        onClick={() => setExpandedOptionMedia(
                                          expandedOptionMedia?.questionId === question.id && expandedOptionMedia?.optionIndex === choiceIndex
                                            ? null
                                            : { questionId: question.id, optionIndex: choiceIndex }
                                        )}
                                      />
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeChoice(question.id, choiceIndex)}
                                      icon={<X className="w-3 h-3" />}
                                    >
                                    </Button>
                                  </>
                                ) : (
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm text-gray-900">{choice}</span>
                                    </div>
                                    {/* Option media preview in read mode */}
                                    {getOptionMediaUrls(question, choiceIndex).length > 0 && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {getOptionMediaUrls(question, choiceIndex).map((url, mediaIdx) => {
                                          const fullUrl = url.startsWith('/api/')
                                            ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                            : url;
                                          const mediaType = getMediaTypeFromUrl(url);
                                          return (
                                            <div key={mediaIdx} className="w-14 h-14 rounded-md border border-gray-200 overflow-hidden bg-gray-50">
                                              {mediaType === 'video' ? (
                                                renderVideoThumb(url, fullUrl)
                                              ) : (
                                                <img
                                                  src={fullUrl}
                                                  alt={`${choice} media ${mediaIdx + 1}`}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                  }}
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Option-level Media Grid for regular layout - show if has images OR is explicitly expanded */}
                              {editingQuestionId === question.id && 
                               supportsMedia &&
                               (getOptionMediaUrls(question, choiceIndex).length > 0 ||
                                (expandedOptionMedia?.questionId === question.id && 
                                 expandedOptionMedia?.optionIndex === choiceIndex)) && (
                                <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                  <div className="text-xs font-medium text-gray-600 mb-2">Option Media</div>
                                  {groupCount > 1 ? (
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={getOptionGroupMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(gi, url) =>
                                        addOptionGroupMedia(question.id, choiceIndex, gi, url)
                                      }
                                      onMediaRemove={(gi, removeIdx) =>
                                        removeOptionGroupMedia(question.id, choiceIndex, gi, removeIdx)
                                      }
                                      onMediaReplace={(gi, mediaIdx, newUrl) =>
                                        replaceOptionGroupMedia(question.id, choiceIndex, gi, mediaIdx, newUrl)
                                      }
                                      maxItemsPerGroup={3}
                                      compact
                                    />
                                  ) : (
                                    <MediaUploadGrid
                                      mediaUrls={getOptionMediaUrls(question, choiceIndex)}
                                      onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                      onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                      onMediaReplace={(index, url) =>
                                        replaceOptionMedia(question.id, choiceIndex, index, url)
                                      }
                                      onMediaReorder={(from, to) =>
                                        reorderOptionMedia(question.id, choiceIndex, from, to)
                                      }
                                      maxItems={3}
                                      compact
                                    />
                                  )}
                                </div>
                              )}

                              {/* Action Display in Read Mode - only show if action exists and not editing */}
                              {(readOnly || editingQuestionId !== question.id) && CONDITIONAL_ACTION_TYPES.includes(question.type) && getChoiceAction(question, choiceIndex)?.action && (
                                <div className="ml-10 mt-1 flex items-center space-x-2">
                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      🚫 Terminates survey
                                      {getChoiceAction(question, choiceIndex)?.reason && ` (${getChoiceAction(question, choiceIndex)?.reason})`}
                                    </span>
                                  )}
                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      ➜ Skips to: {questions.find(q => q.id === getChoiceAction(question, choiceIndex)?.target)?.text.substring(0, 40) || 'Unknown question'}...
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Action Configuration for Regular Layout - only show in edit mode for conditional action types */}
                              {editingQuestionId === question.id && CONDITIONAL_ACTION_TYPES.includes(question.type) && (
                                <div className="ml-10 flex items-center space-x-2">
                                  <label className="text-xs text-gray-600 min-w-fit">Action:</label>
                                  <select
                                    value={getChoiceAction(question, choiceIndex)?.action || 'none'}
                                    onChange={(e) => {
                                      const action = e.target.value === 'none' ? undefined : e.target.value as 'terminate' | 'skip_to';
                                      updateChoiceAction(question.id, choiceIndex, action);
                                    }}
                                    className="px-2 py-1 border border-gray-300 rounded text-xs"
                                  >
                                    <option value="none">Continue normally</option>
                                    <option value="terminate">Terminate (disqualify)</option>
                                    <option value="skip_to">Skip to question</option>
                                  </select>

                                  {getChoiceAction(question, choiceIndex)?.action === 'skip_to' && (
                                    <select
                                      value={getChoiceAction(question, choiceIndex)?.target || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'skip_to', e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs"
                                    >
                                      <option value="">Select question...</option>
                                      {questions.map(q => (
                                        <option key={q.id} value={q.id}>{q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}</option>
                                      ))}
                                    </select>
                                  )}

                                  {getChoiceAction(question, choiceIndex)?.action === 'terminate' && (
                                    <input
                                      type="text"
                                      value={getChoiceAction(question, choiceIndex)?.reason || ''}
                                      onChange={(e) => updateChoiceAction(question.id, choiceIndex, 'terminate', undefined, e.target.value)}
                                      placeholder="Reason (optional)"
                                      className="px-2 py-1 border border-gray-300 rounded text-xs flex-1"
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Show "Other" indicator when allow_other is enabled - only for single_choice and multiple_choice WITHOUT media */}
                    {question.allow_other && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') &&
                     (!question.media_urls || question.media_urls.length === 0) && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          +
                        </span>
                        <span className="text-sm text-gray-600">
                          {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.OTHER_OPTION_INDICATOR}
                        </span>
                      </div>
                    )}

                    {/* Show "Randomized" indicator when randomize_choices is enabled - for single_choice and multiple_choice */}
                    {question.randomize_choices && editingQuestionId !== question.id &&
                     (question.type === 'single_choice' || question.type === 'multiple_choice') && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-sm text-gray-500 w-8">
                          ~
                        </span>
                        <span className="text-sm text-gray-600">
                          Choices will be randomized
                        </span>
                      </div>
                    )}
                    {(question.choices || []).length === 0 && editingQuestionId !== question.id && (
                      <span className="text-sm text-gray-500 italic">No options yet</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (editingQuestionId !== question.id) {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }
                        addChoice(question.id);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.BUTTONS.ADD_CHOICE}
                    </Button>
                    {editingQuestionId === question.id && (
                      <div className="space-y-2">
                        {/* Allow Other Option Toggle - only for single_choice and multiple_choice WITHOUT media */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') &&
                         (!question.media_urls || question.media_urls.length === 0) && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`allow-other-${question.id}`}
                              checked={question.allow_other || false}
                              // "Other" is a selectable option, so switching it off shrinks what a
                              // cap can reach — route it through the re-clamping patcher.
                              onChange={(e) => updateQuestionOptions(question.id, { allow_other: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`allow-other-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.ALLOW_OTHER_OPTION}
                            </label>
                          </div>
                        )}

                        {/* Randomize Responses Toggle - only for single_choice and multiple_choice */}
                        {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                          <div className="flex items-center space-x-2 pt-2">
                            <input
                              type="checkbox"
                              id={`randomize-choices-${question.id}`}
                              checked={question.randomize_choices || false}
                              onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label
                              htmlFor={`randomize-choices-${question.id}`}
                              className="text-sm text-gray-700 cursor-pointer"
                            >
                              Randomize Responses
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scale settings for scale-based questions */}
              {hasScale(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scale Settings
                  </label>
                  {editingQuestionId === question.id ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_min || (question.type === 'nps' ? 0 : 1)}
                          onChange={(e) => updateQuestion(question.id, { scale_min: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_max || (question.type === 'nps' ? 10 : 5)}
                          onChange={(e) => updateQuestion(question.id, { scale_max: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                          min={question.type === 'nps' ? 0 : 1}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4 text-sm text-gray-700">
                      <span>
                        <span className="font-medium">Min:</span> {question.scale_min || (question.type === 'nps' ? 0 : 1)}
                      </span>
                      <span>
                        <span className="font-medium">Max:</span> {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Ranking settings for ranking questions */}
              {hasRanking(question.type) && editingQuestionId === question.id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ranking Settings
                  </label>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Number of items to rank
                    </label>
                    <input
                      type="number"
                      value={question.ranking_count || 3}
                      onChange={(e) => updateQuestion(question.id, { ranking_count: parseInt(e.target.value) })}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                      min={1}
                      max={question.choices?.length || 5}
                    />
                  </div>
                </div>
              )}

              {/* Selection cap for multi-select questions (spec 36). Blank = no limit; the field
                  already existed in the data and could only be set by the AI parser, never seen or
                  changed by the author.

                  The read-only half is gated on there being something to READ. Gating the whole
                  block on the question TYPE put a permanent "Maximum selections: No limit" row on
                  every multi-select that has no cap — 223 of them live on PROD — which is a line
                  of chrome announcing the absence of a setting on questions the setting was never
                  applied to. The EDITOR is not gated: a blank field is how a cap gets set. */}
              {isMultiSelect(question.type)
                && (editingQuestionId === question.id
                  || selectionCap(question) !== null
                  || repairedSelectionCapNote(question) !== null) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selection Settings
                  </label>
                  {editingQuestionId === question.id ? (
                    <div>
                      <label
                        className="block text-xs text-gray-600 mb-1"
                        htmlFor={`max-selections-${question.id}`}
                      >
                        {MAX_SELECTIONS_LABEL}
                      </label>
                      <input
                        id={`max-selections-${question.id}`}
                        type="number"
                        // The author's REQUEST, so every keystroke moves something on screen. See
                        // `editorSelectionCap`; what is stored is still the bounded number.
                        value={editorSelectionCap(question)}
                        onChange={(e) => {
                          // Remember the number as typed, store it bounded. The bound is what a
                          // half-finished option list would otherwise make permanent.
                          const requested = parseMaxSelections(e.target.value);
                          setRequestedSelectionCaps((prev) => {
                            const next = { ...prev };
                            if (requested === null) delete next[question.id];
                            else next[question.id] = requested;
                            return next;
                          });
                          updateQuestion(question.id, {
                            max_selections: storedSelectionCap(
                              requested, maxSelectableOptions(question)),
                          });
                        }}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        placeholder={MAX_SELECTIONS_PLACEHOLDER}
                        min={1}
                        max={maxSelectableOptions(question)}
                      />
                      <p className="mt-1 text-xs text-gray-500">{MAX_SELECTIONS_HELP}</p>
                      {boundedSelectionCapNote(question) && (
                        <p className="mt-1 text-xs text-amber-600">
                          {boundedSelectionCapNote(question)}
                        </p>
                      )}
                      {repairedSelectionCapNote(question) && (
                        <p className="mt-1 text-xs text-amber-600">
                          {repairedSelectionCapNote(question)}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{MAX_SELECTIONS_LABEL}:</span>{' '}
                      {/* Read through the RUNTIME predicate, never off the raw field: a stale 0 or
                          negative left by an older parse is ignored when the survey runs, so
                          printing it here would claim a limit that is not in force. */}
                      {selectionCap(question) ?? MAX_SELECTIONS_NONE}
                      {repairedSelectionCapNote(question) && (
                        <p className="mt-1 text-xs text-amber-600">
                          {repairedSelectionCapNote(question)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Matrix settings for matrix_rating questions */}
              {hasMatrix(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Matrix/Grid Settings
                  </label>

                  {/* Matrix Items */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Items to Rate
                    </label>
                    <div className="space-y-2">
                      {(question.matrix_rows || []).map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {itemIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => updateMatrixItem(question.id, itemIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Item to rate"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeMatrixItem(question.id, itemIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{item}</span>
                          )}
                        </div>
                      ))}

                      {(!question.matrix_rows || question.matrix_rows.length === 0) && editingQuestionId !== question.id && (
                        <span className="text-sm text-gray-500 italic">No items defined</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingQuestionId !== question.id) {
                            setOriginalQuestionBeforeEdit(question);
                            setEditingQuestionId(question.id);
                          }
                          addMatrixItem(question.id);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Add Matrix Item
                      </Button>
                    </div>
                  </div>

                  {/* Scale Labels */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Scale Labels
                    </label>
                    {editingQuestionId === question.id ? (
                      <input
                        type="text"
                        value={question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                        onChange={(e) => updateQuestion(question.id, {
                          matrix_columns: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                        })}
                        className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        placeholder="1, 2, 3, 4, 5"
                      />
                    ) : (
                      <div className="text-sm text-gray-900">
                        {question.matrix_columns?.join(', ') || '1, 2, 3, 4, 5'}
                      </div>
                    )}
                  </div>

                  {/* Randomize Items Toggle - only in edit mode */}
                  {editingQuestionId === question.id && (
                    <div className="flex items-center space-x-2 pt-2">
                      <input
                        type="checkbox"
                        id={`randomize-matrix-${question.id}`}
                        checked={question.randomize_choices || false}
                        onChange={(e) => updateQuestion(question.id, { randomize_choices: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`randomize-matrix-${question.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Randomize Items (scale labels remain fixed)
                      </label>
                    </div>
                  )}

                  {/* Show "Randomized" indicator when randomize_choices is enabled */}
                  {question.randomize_choices && editingQuestionId !== question.id && (
                    <div className="flex items-center space-x-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-500">
                        ~
                      </span>
                      <span className="text-sm text-gray-600">
                        Items will be randomized (scale labels remain fixed)
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Percentage Allocation settings */}
              {hasAllocation(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Percentage Allocation Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      Respondents will allocate percentages across the options. The total must equal the target (default: 100%).
                    </p>
                  )}

                  {/* Allocation Options */}
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">
                      Options to Allocate
                    </label>
                    <div className="space-y-2">
                      {(question.allocation_options || question.choices || []).map((option: string, optionIndex: number) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-8">
                            {optionIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => {
                                  const options = [...(question.allocation_options || question.choices || [])];
                                  options[optionIndex] = e.target.value;
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Option name"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const options = (question.allocation_options || question.choices || []).filter((_: string, i: number) => i !== optionIndex);
                                  updateQuestion(question.id, { allocation_options: options, choices: options });
                                }}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{option}</span>
                          )}
                        </div>
                      ))}

                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const options = [...(question.allocation_options || question.choices || []), `Option ${(question.allocation_options || question.choices || []).length + 1}`];
                            updateQuestion(question.id, { allocation_options: options, choices: options });
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Option
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Allocation Target */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Target Total
                      </label>
                      <input
                        type="number"
                        value={question.allocation_target || 100}
                        onChange={(e) => updateQuestion(question.id, { allocation_target: parseInt(e.target.value) || 100 })}
                        className="w-24 px-3 py-1 border border-gray-300 rounded text-sm"
                        min={1}
                        max={1000}
                      />
                    </div>
                  )}

                  {/* Auto-fill Last & Allow Zero toggles */}
                  {editingQuestionId === question.id && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`auto-fill-${question.id}`}
                          checked={question.auto_fill_last !== false}
                          onChange={(e) => updateQuestion(question.id, { auto_fill_last: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`auto-fill-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Auto-calculate last option
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`allow-zero-${question.id}`}
                          checked={question.allow_zero !== false}
                          onChange={(e) => updateQuestion(question.id, { allow_zero: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`allow-zero-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Allow 0 for individual options
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Show settings summary when not editing */}
                  {editingQuestionId !== question.id && (
                    <div className="text-sm text-gray-600 mt-2">
                      Target: {question.allocation_target || 100}
                      {question.auto_fill_last !== false && ' • Auto-fill last option'}
                      {question.allow_zero === false && ' • No zeros allowed'}
                    </div>
                  )}
                </div>
              )}

              {/* MaxDiff settings for max_diff questions */}
              {hasMaxDiff(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Settings
                  </label>

                  {editingQuestionId === question.id && (
                    <p className="text-xs text-gray-500 mb-2">
                      MaxDiff questions present respondents with sets of options where they select the most and least appealing choices. Edit the claims/options below.
                    </p>
                  )}

                  {/* MaxDiff Claims/Options */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Claims/Options
                    </label>
                    <div className="space-y-2">
                      {(question.choices || []).map((choice, choiceIndex) => {
                        // MaxDiff items live in `choices`, so their images reuse the per-choice
                        // `option_media` sidecar directly — same indexes, no new field.
                        const itemMediaUrls = getOptionMediaUrls(question, choiceIndex);
                        // ...and, in a grouped survey, the per-group sidecar the choice rows
                        // already use. `_build_questions_for_group` resolves it back down to
                        // `option_media` for the group being served, so the respondent runtime
                        // needs nothing: an A/B packaging MaxDiff is an authoring feature.
                        const itemGroupMedia = getOptionGroupMediaUrls(question, choiceIndex);
                        const itemGroupMediaCount = countGroupMedia(itemGroupMedia);
                        const usesGroupMedia = groupCount > 1;
                        // A grouped survey can still be SERVING a shared image: a study
                        // illustrated before it was split into A/B has its claim images on
                        // `option_media`, and `_build_questions_for_group` only replaces that key
                        // for a group some option has an entry for. Following "whichever sidecar
                        // the survey uses" therefore hid images respondents were being shown, with
                        // no way to remove them. Both are surfaced, and the shared one says who
                        // still gets it.
                        const hasStrandedSharedMedia = usesGroupMedia && itemMediaUrls.length > 0;
                        // Rendered next to the shared image in whichever mode is on screen — never
                        // both at once, since the row is either the editor or the read view.
                        const strandedSharedMediaNote = hasStrandedSharedMedia ? (
                          <div className="mt-1 text-[11px] text-gray-600">
                            {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_ITEM_MEDIA_SHARED_IN_GROUPS}
                          </div>
                        ) : null;
                        // The respondent renders ONE item — the first that is not a video.
                        // `maxItems` blocks ADDING past the cap but never trims what is already
                        // stored, and retyping a single_choice question as max_diff carries its
                        // multi-item option_media across untouched, so a preview of all of them
                        // would promise the researcher images no respondent will ever see.
                        const shownItemMediaUrls = shownMaxDiffItemMedia(itemMediaUrls);
                        // Both sidecars, because both are served: the icon is the only thing a
                        // collapsed row says about whether a claim is illustrated at all.
                        const itemMediaCount = itemMediaUrls.length + (usesGroupMedia ? itemGroupMediaCount : 0);
                        const isItemMediaExpanded =
                          expandedOptionMedia?.questionId === question.id &&
                          expandedOptionMedia?.optionIndex === choiceIndex;

                        return (
                          <div key={choiceIndex} className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500 w-8">
                                {choiceIndex + 1}.
                              </span>
                              {editingQuestionId === question.id ? (
                                <>
                                  <input
                                    type="text"
                                    value={choice}
                                    onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                    className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Enter claim/option"
                                  />
                                  <MediaUploadIcon
                                    // Counts whichever sidecar this survey actually writes to:
                                    // read off `option_media` alone, the icon reads "no image" for
                                    // every claim of a grouped survey, and the collapsed row is
                                    // the only place that says a claim is illustrated at all.
                                    hasMedia={itemMediaCount > 0}
                                    mediaCount={itemMediaCount}
                                    onClick={() => setExpandedOptionMedia(
                                      isItemMediaExpanded
                                        ? null
                                        : { questionId: question.id, optionIndex: choiceIndex }
                                    )}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeChoice(question.id, choiceIndex)}
                                    icon={<X className="w-3 h-3" />}
                                  >
                                  </Button>
                                </>
                              ) : (
                                <div className="flex-1">
                                  <span className="text-sm text-gray-900">{choice}</span>
                                  {/* Each group's own pack shot, under its label. Without this the
                                      read view of a grouped MaxDiff shows no picture at all and
                                      reads exactly like a claim nobody illustrated. */}
                                  {usesGroupMedia && itemGroupMediaCount > 0 && (
                                    <div className="mt-2 space-y-1.5">
                                      {(question.visible_groups ?? Array.from({ length: groupCount }, (_, i) => i)).map((gi) => {
                                        const urls = itemGroupMedia[String(gi)];
                                        if (!urls || urls.length === 0) return null;
                                        const color = getGroupColor(gi);
                                        return (
                                          <div key={gi} className={`rounded border ${color.border} ${color.bg} px-2 py-1 flex items-center gap-2`}>
                                            <span className={`text-[9px] font-semibold uppercase ${color.text} shrink-0`}>{getGroupLabel(gi)}</span>
                                            <div className="flex flex-wrap gap-1">
                                              {shownMaxDiffItemMedia(urls).map((url, mediaIdx) => {
                                                const fullUrl = url.startsWith('/api/')
                                                  ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                                  : url;
                                                return (
                                                  <div key={mediaIdx} className={MAX_DIFF_ITEM_THUMB_CLASS}>
                                                    {getMediaTypeFromUrl(url) === 'video' ? (
                                                      renderVideoThumb(url, fullUrl)
                                                    ) : (
                                                      <img
                                                        src={fullUrl}
                                                        alt={`${getGroupLabel(gi)} ${choice} image`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                          (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                      />
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {itemMediaUrls.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {shownItemMediaUrls.map((url, mediaIdx) => {
                                        const fullUrl = url.startsWith('/api/')
                                          ? `${import.meta.env.VITE_API_URL || ''}${url}`
                                          : url;
                                        return (
                                          <div key={mediaIdx} className={MAX_DIFF_ITEM_THUMB_CLASS}>
                                            {getMediaTypeFromUrl(url) === 'video' ? (
                                              renderVideoThumb(url, fullUrl)
                                            ) : (
                                              <img
                                                src={fullUrl}
                                                alt={`${choice} image`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                  (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                              />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {strandedSharedMediaNote}
                                </div>
                              )}
                            </div>

                            {/* Item media grid — open when the claim already has an image, or on request */}
                            {editingQuestionId === question.id &&
                             (itemMediaCount > 0 || isItemMediaExpanded) && (
                              <div className="ml-10 p-2 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="text-xs font-medium text-gray-600 mb-2">
                                  {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_ITEM_MEDIA}
                                </div>
                                {usesGroupMedia ? (
                                  <>
                                    <GroupMediaUpload
                                      groupCount={groupCount}
                                      visibleGroups={question.visible_groups}
                                      groupMedia={itemGroupMedia}
                                      onMediaAdd={(gi, url) => addOptionGroupMedia(question.id, choiceIndex, gi, url)}
                                      onMediaRemove={(gi, index) =>
                                        removeOptionGroupMedia(question.id, choiceIndex, gi, index)
                                      }
                                      onMediaReplace={(gi, index, url) =>
                                        replaceOptionGroupMedia(question.id, choiceIndex, gi, index, url)
                                      }
                                      maxItemsPerGroup={MAX_DIFF_ITEM_MEDIA_LIMIT}
                                      allowVideo={false}
                                      compact
                                    />
                                    {hasStrandedSharedMedia && (
                                      <div className="mt-2">
                                        <MediaUploadGrid
                                          mediaUrls={itemMediaUrls}
                                          onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                          onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                          onMediaReplace={(index, url) =>
                                            replaceOptionMedia(question.id, choiceIndex, index, url)
                                          }
                                          onMediaReorder={(from, to) =>
                                            reorderOptionMedia(question.id, choiceIndex, from, to)
                                          }
                                          // Exactly what is already stored, so this control can
                                          // show and clear it but not grow it: a new image here
                                          // would be dropped for any group that has one of its own,
                                          // and the per-group control above is where images go now.
                                          maxItems={itemMediaUrls.length}
                                          allowVideo={false}
                                          compact
                                        />
                                        {strandedSharedMediaNote}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <MediaUploadGrid
                                    mediaUrls={itemMediaUrls}
                                    onMediaAdd={(url) => addOptionMedia(question.id, choiceIndex, url)}
                                    onMediaRemove={(index) => removeOptionMedia(question.id, choiceIndex, index)}
                                    onMediaReplace={(index, url) =>
                                      replaceOptionMedia(question.id, choiceIndex, index, url)
                                    }
                                    onMediaReorder={(from, to) =>
                                      reorderOptionMedia(question.id, choiceIndex, from, to)
                                    }
                                    maxItems={MAX_DIFF_ITEM_MEDIA_LIMIT}
                                    // The tile a respondent ranks IS a <button>, so a playable
                                    // <video> cannot live inside it — and eight players at once
                                    // (4 items x 2 columns) is a different feature anyway. Refused
                                    // here rather than accepted and silently dropped later.
                                    allowVideo={false}
                                    compact
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <div className="space-y-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (editingQuestionId !== question.id) {
                              setOriginalQuestionBeforeEdit(question);
                              setEditingQuestionId(question.id);
                            }
                            addChoice(question.id);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Add Claim/Option
                        </Button>
                      </div>
                    </div>

                    {/* Duplicate claims. Shown in BOTH modes: the researcher creates the
                        duplicate while editing, and inherits it when a question is duplicated. */}
                    {hasDuplicateMaxDiffClaims(question.choices) && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          <span aria-hidden="true">⚠️ </span>
                          <span>{UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_DUPLICATE_CLAIMS}</span>
                        </div>
                      </div>
                    )}

                    {/* Claims that read the same but are stored apart. Its own note because the
                        consequences are the opposite ones: two images, two design slots, one
                        claim as far as the respondent can see. */}
                    {hasLookalikeMaxDiffClaims(question.choices) && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          <span aria-hidden="true">⚠️ </span>
                          <span>{UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_LOOKALIKE_CLAIMS}</span>
                        </div>
                      </div>
                    )}

                    {/* Images stored beyond the one-per-claim cap are not shown to respondents. */}
                    {hasSurplusMaxDiffItemMedia(question) && (
                      <div className="mt-2 text-xs text-gray-600">
                        {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_ITEM_MEDIA_FIRST_ONLY}
                      </div>
                    )}

                    {/* One arm of the A/B being served a blank tile. See optionGroupMediaGaps. */}
                    {optionGroupMediaGaps(question, groupCount).map(({ groupIndex, optionIndexes }) => (
                      <div key={groupIndex} className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          <span aria-hidden="true">⚠️ </span>
                          <span>
                            {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.OPTION_GROUP_MEDIA_GAP(
                              getGroupLabel(groupIndex),
                              optionIndexes.map((index) => question.choices?.[index] ?? `#${index + 1}`),
                            )}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* A video carried over from another question type renders as nothing at all
                        for the respondent, so it has to be visible where it can be replaced. */}
                    {hasMaxDiffItemVideo(question) && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          <span aria-hidden="true">⚠️ </span>
                          <span>{UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.MAX_DIFF_ITEM_MEDIA_IMAGE_ONLY}</span>
                        </div>
                      </div>
                    )}

                    {/* Information about BIBD generation */}
                    {question.choices && question.choices.length > 0 && editingQuestionId !== question.id && (
                      <div className="mt-2 text-xs text-blue-600">
                        ℹ️ {question.choices.length} claims will be shown in balanced sets of 4 using MaxDiff algorithm (balanced item frequency & pair co-occurrence for statistical validity)
                      </div>
                    )}

                    {(!question.choices || question.choices.length === 0) && editingQuestionId !== question.id && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                        <div className="text-xs text-yellow-800">
                          ⚠️ No claims/options found. MaxDiff requires multiple options to compare.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Conjoint Analysis settings for conjoint_analysis questions */}
              {hasConjoint(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conjoint Analysis Settings
                  </label>

                  {/* Conjoint Description */}
                  {editingQuestionId === question.id && (
                    <div className="mb-4">
                      <label className="block text-xs text-gray-600 mb-1">
                        Question Description
                      </label>
                      <textarea
                        value={question.description || ''}
                        onChange={(e) => updateQuestion(question.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm resize-none"
                        rows={2}
                        placeholder="Additional context or instructions for this question"
                      />
                    </div>
                  )}

                  {/* Conjoint Attributes */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Attributes & Levels
                    </label>
                    <div className="space-y-3">
                      {(question.conjoint_attributes || []).map((attr, attrIndex) => (
                        <div key={attrIndex} className="border border-gray-200 rounded p-3 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              Attribute {attrIndex + 1}
                            </span>
                            {editingQuestionId === question.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeConjointAttribute(question.id, attrIndex)}
                                icon={<X className="w-3 h-3" />}
                              >
                              </Button>
                            )}
                          </div>

                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={attr.name}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'name', e.target.value)}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm mb-2"
                                placeholder="Attribute name (e.g., 'Price', 'Brand')"
                              />
                              <textarea
                                value={attr.levels.join(', ')}
                                onChange={(e) => updateConjointAttribute(question.id, attrIndex, 'levels',
                                  e.target.value.split(',').map(s => s.trim()).filter(s => s)
                                )}
                                className="w-full px-3 py-1 border border-gray-300 rounded text-sm resize-none"
                                rows={2}
                                placeholder="Levels (comma-separated, e.g., '$99, $199, $299')"
                              />
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900 mb-1">{attr.name}</div>
                              <div className="text-sm text-gray-600">
                                {attr.levels.join(', ')}
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {(!question.conjoint_attributes || question.conjoint_attributes.length === 0) && editingQuestionId !== question.id && (
                        <span className="text-sm text-gray-500 italic">No attributes defined</span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (editingQuestionId !== question.id) {
                            setOriginalQuestionBeforeEdit(question);
                            setEditingQuestionId(question.id);
                          }
                          addConjointAttribute(question.id);
                        }}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Add Attribute
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Image Hotspot settings for image_hotspot questions */}
              {hasImageHotspot(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Hotspot Settings
                  </label>
                  
                  {/* Always show editor for image_hotspot if no image, otherwise check edit mode */}
                  {(editingQuestionId === question.id || (!question.image_url && !readOnly)) ? (
                    <ImageHotspotEditor
                      config={{
                        image_url: question.image_url || '',
                        image_path: question.image_path || '',
                        ranking_count: question.ranking_count || 3,
                        bounding_boxes: question.bounding_boxes || [],
                        free_click_mode: question.free_click_mode || false,
                        fit_to_screen: question.fit_to_screen,
                        custom_size: question.custom_size,
                        custom_width_vw: question.custom_width_vw,
                        custom_height_vh: question.custom_height_vh,
                      }}
                      onChange={(config: ImageHotspotConfig) => {
                        // FIRST: If uploading image to a question not in edit mode, enter edit mode
                        // This must happen before updateQuestion to prevent editor from closing
                        if (editingQuestionId !== question.id) {
                          setOriginalQuestionBeforeEdit(question);
                          setEditingQuestionId(question.id);
                        }
                        // THEN: Update the question data
                        updateQuestion(question.id, {
                          image_url: config.image_url,
                          image_path: config.image_path,
                          ranking_count: config.ranking_count,
                          bounding_boxes: config.bounding_boxes,
                          free_click_mode: config.free_click_mode,
                          fit_to_screen: config.fit_to_screen,
                          custom_size: config.custom_size,
                          custom_width_vw: config.custom_width_vw,
                          custom_height_vh: config.custom_height_vh,
                        });
                      }}
                      disabled={false}
                    />
                  ) : (
                    <div className="space-y-3">
                      {question.image_url ? (() => {
                        // Build full image URL (same logic as ImageHotspotEditor)
                        const API_URL = import.meta.env.VITE_API_URL || '';
                        const fullImageUrl = question.image_url.startsWith('http') 
                          ? question.image_url 
                          : `${API_URL}${question.image_url}`;
                        return (
                          <>
                            {/* Image Preview with Bounding Boxes */}
                            <div className="relative inline-block">
                              <img
                                src={fullImageUrl}
                                alt="Hotspot Preview"
                                className="max-w-md h-auto rounded-lg border border-gray-200"
                                onLoad={(e) => {
                                  const img = e.currentTarget;
                                  img.dataset.width = img.clientWidth.toString();
                                  img.dataset.height = img.clientHeight.toString();
                                }}
                              />
                              {/* Render bounding boxes on image (only for region-based mode) */}
                              {!question.free_click_mode && question.bounding_boxes?.map((box, idx) => (
                                <div
                                  key={box.id || idx}
                                  className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                                  style={{
                                    left: `${box.x * 100}%`,
                                    top: `${box.y * 100}%`,
                                    width: `${box.width * 100}%`,
                                    height: `${box.height * 100}%`
                                  }}
                                >
                                  <div className="absolute -top-5 left-0 bg-teal-500 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
                                    {box.label}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {/* Summary info */}
                            <div className="text-sm text-gray-600">
                              {question.free_click_mode ? (
                                <>
                                  <span className="text-teal-600">✓</span> Free clicking mode enabled
                                  <span className="mx-2">•</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-green-600">✓</span> {question.bounding_boxes?.length || 0} product region(s) defined
                                  <span className="mx-2">•</span>
                                </>
                              )}
                              Respondents select top <span className="font-medium">{question.ranking_count || 3}</span> {question.free_click_mode ? 'location(s)' : 'choice(s)'}
                            </div>
                          </>
                        );
                      })() : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <div className="text-sm text-yellow-800">
                            ⚠️ No image uploaded. Click Edit to add an image and define product regions.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Photo Upload settings for photo_upload questions */}
              {hasPhotoUpload(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Photo Upload Settings
                  </label>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-500">
                        Respondents will upload photos of their pantry or fridge. AI will automatically extract visible products and brands.
                      </p>

                      {/* Max Images Setting */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Maximum Images
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={question.max_images || 3}
                            onChange={(e) => {
                              const value = Math.max(1, Math.min(10, parseInt(e.target.value) || 3));
                              updateQuestion(question.id, { max_images: value });
                            }}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-teal-500"
                          />
                          <span className="text-xs text-gray-500">photos per response (1-10)</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <span className="text-blue-600">📸</span> Max <span className="font-medium">{question.max_images || 3}</span> photo(s) with AI product extraction
                    </div>
                  )}
                </div>
              )}

              {/* Info Screen settings for info_screen questions */}
              {question.type === 'info_screen' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Info Screen Settings
                  </label>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      {/* Display Time Setting */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Minimum Display Time (seconds)
                        </label>
                        <div className="flex items-center space-x-3">
                          <input
                            type="number"
                            min="0"
                            max="300"
                            value={question.display_time_seconds || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value) : undefined;
                              updateQuestion(question.id, { display_time_seconds: value });
                            }}
                            className="w-24 px-3 py-2 border border-gray-300 rounded text-sm"
                            placeholder="0"
                          />
                          <span className="text-sm text-gray-500">seconds</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          If set, the Continue button will be disabled until the timer completes. Leave empty for no timer.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm text-gray-600">
                      {question.display_time_seconds ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-blue-600">⏱</span>
                          <span>Display time: <span className="font-medium">{question.display_time_seconds}</span> seconds</span>
                        </div>
                      ) : (
                        <div className="text-gray-500">No minimum display time set</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* AI Follow-up Toggle - Hidden for Conjoint Analysis and for AI Dynamic Section
                  (which has its own adaptive-probing rubric via the panel above — this generic
                  toggle would be a second, contradictory switch for the same idea). */}
              {question.type !== 'conjoint_analysis' && question.type !== 'ai_dynamic_section' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      AI Follow-up Questions
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-ai-followup-${question.id}`}
                          checked={question.enable_ai_followup !== false}
                          onChange={(e) => updateQuestion(question.id, { enable_ai_followup: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-ai-followup-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Enable AI follow-up questions
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.enable_ai_followup
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.enable_ai_followup ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    {question.type === 'max_diff'
                      ? 'When enabled, AI will ask follow-up questions about the top 3 most appealing options after all MaxDiff sets are completed.'
                      : 'When enabled, AI may ask follow-up questions based on the respondent\'s answer to get deeper insights.'}
                  </p>
                </div>
              )}

              {/* MaxDiff mode (spec 36-02): today's multi-set behaviour, or one-screen
                  best/worst with N best and N worst. Shown only for max_diff questions; the
                  n_best/n_worst inputs are shown only once best/worst is selected. */}
              {question.type === 'max_diff' && (
                <div data-setting-key="max_diff_config">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Mode
                  </label>
                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="radio"
                            name={`maxdiff-mode-${question.id}`}
                            checked={question.max_diff_config?.mode !== MAXDIFF_MODE_BEST_WORST}
                            onChange={() => updateQuestion(question.id, {
                              max_diff_config: { mode: MAXDIFF_MODE_MULTI_SET },
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          {MAXDIFF_MULTI_SET_MODE_LABEL}
                        </label>
                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="radio"
                            name={`maxdiff-mode-${question.id}`}
                            checked={question.max_diff_config?.mode === MAXDIFF_MODE_BEST_WORST}
                            onChange={() => updateQuestion(question.id, {
                              max_diff_config: {
                                mode: MAXDIFF_MODE_BEST_WORST,
                                n_best: question.max_diff_config?.n_best ?? DEFAULT_BEST_WORST_QUOTA,
                                n_worst: question.max_diff_config?.n_worst ?? DEFAULT_BEST_WORST_QUOTA,
                                capture_rank: true,
                              },
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          {MAXDIFF_BEST_WORST_MODE_LABEL}
                        </label>
                      </div>
                      {question.max_diff_config?.mode === MAXDIFF_MODE_BEST_WORST && (
                        <>
                          <p className="text-xs text-gray-500">{MAXDIFF_BEST_WORST_HELP}</p>
                          <div className="flex items-center gap-4">
                            <div>
                              <label htmlFor={`n-best-${question.id}`} className="block text-xs font-medium text-gray-600 mb-1">
                                {MAXDIFF_N_BEST_LABEL}
                              </label>
                              <input
                                type="number"
                                id={`n-best-${question.id}`}
                                min={0}
                                value={question.max_diff_config?.n_best ?? 0}
                                onChange={(e) => updateQuestion(question.id, {
                                  max_diff_config: {
                                    ...question.max_diff_config,
                                    mode: MAXDIFF_MODE_BEST_WORST,
                                    n_best: Number(e.target.value) || 0,
                                    capture_rank: true,
                                  },
                                })}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                            <div>
                              <label htmlFor={`n-worst-${question.id}`} className="block text-xs font-medium text-gray-600 mb-1">
                                {MAXDIFF_N_WORST_LABEL}
                              </label>
                              <input
                                type="number"
                                id={`n-worst-${question.id}`}
                                min={0}
                                value={question.max_diff_config?.n_worst ?? 0}
                                onChange={(e) => updateQuestion(question.id, {
                                  max_diff_config: {
                                    ...question.max_diff_config,
                                    mode: MAXDIFF_MODE_BEST_WORST,
                                    n_worst: Number(e.target.value) || 0,
                                    capture_rank: true,
                                  },
                                })}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {bestWorstModeSummary(question)}
                    </span>
                  )}
                </div>
              )}

              {/* Input mode - voice/text controls for open-ended text questions only */}
              {(question.type === 'short_text' || question.type === 'long_text') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Input Mode
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Respondents can answer with both voice and text by default. Disable one to require the other. Both cannot be disabled.
                  </p>
                  {editingQuestionId === question.id ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`disable-voice-${question.id}`}
                          checked={!!question.disable_voice_input}
                          onChange={(e) => updateQuestion(question.id, {
                            disable_voice_input: e.target.checked,
                            disable_text_input: e.target.checked ? false : question.disable_text_input,
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`disable-voice-${question.id}`} className="text-sm text-gray-700 cursor-pointer">
                          Disable voice input (text only)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`disable-text-${question.id}`}
                          checked={!!question.disable_text_input}
                          onChange={(e) => updateQuestion(question.id, {
                            disable_text_input: e.target.checked,
                            disable_voice_input: e.target.checked ? false : question.disable_voice_input,
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor={`disable-text-${question.id}`} className="text-sm text-gray-700 cursor-pointer">
                          Disable text input (voice only)
                        </label>
                      </div>
                    </div>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {question.disable_voice_input
                        ? 'Text only'
                        : question.disable_text_input
                        ? 'Voice only'
                        : 'Voice & text'}
                    </span>
                  )}
                </div>
              )}

              {/* Follow-up instructions - only show when AI follow-up is enabled. Also excluded
                  for AI Dynamic Section defensively: the toggle above can no longer turn this on
                  for that type, but a question switched FROM another type could still carry a
                  stale enable_ai_followup=true, and this instructions field is not the one this
                  section reads (probing_instructions, in the panel above, is). */}
              {question.type !== 'ai_dynamic_section' && question.enable_ai_followup && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS}
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.DESCRIPTIONS.FOLLOWUP_INSTRUCTIONS_HELP}
                  </p>
                  {editingQuestionId === question.id ? (
                    <textarea
                      value={question.followup_instructions || ''}
                      onChange={(e) => updateQuestion(question.id, { followup_instructions: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none text-sm"
                      rows={2}
                      placeholder={UI_TEXT.HUMAN_SURVEY.QUESTIONS_PREVIEW.LABELS.FOLLOWUP_INSTRUCTIONS_PLACEHOLDER}
                    />
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 min-h-[2.5rem]">
                      {question.followup_instructions ? (
                        <span className="text-sm text-gray-900">{question.followup_instructions}</span>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No specific follow-up instructions</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Probing rubric (optional) — drives rubric-based adaptive follow-up probing.
                  Excluded for AI Dynamic Section for the same reason as Follow-up instructions
                  above: that type has its own rubric, edited in the panel above, in a different
                  shape (AiDynamicSectionRubricPoint[], not this field's QuestionRubric). */}
              {question.type !== 'ai_dynamic_section' && question.enable_ai_followup && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Probing rubric (optional)
                    </label>
                    <span className="group relative inline-flex items-center" tabIndex={0}>
                      <Info className="w-3.5 h-3.5 text-gray-400" />
                      <span className="pointer-events-none absolute left-1/2 bottom-full z-10 mb-1 hidden w-64 -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg group-hover:block group-focus:block">
                        {RUBRIC_INFO_TEXT}
                      </span>
                    </span>
                  </div>

                  {editingQuestionId === question.id ? (
                    <div className="space-y-3">
                      <textarea
                        value={question.rubric?.information_goal || ''}
                        onChange={(e) =>
                          updateQuestion(question.id, {
                            rubric: { ...(question.rubric || {}), information_goal: e.target.value },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none text-sm"
                        rows={2}
                        placeholder="What should a great answer reveal? Leave blank to auto-derive on save."
                      />

                      {question.rubric?.points && question.rubric.points.length > 0 && (
                        <div className="space-y-2">
                          {question.rubric.points.map((point, pIdx) => (
                            <div key={pIdx} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={point.text}
                                onChange={(e) => {
                                  const points = [...(question.rubric?.points || [])];
                                  points[pIdx] = { ...points[pIdx], text: e.target.value };
                                  updateQuestion(question.id, { rubric: { ...(question.rubric || {}), points } });
                                }}
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                                placeholder="Point to probe for"
                              />
                              <select
                                value={point.tier}
                                onChange={(e) => {
                                  const points = [...(question.rubric?.points || [])];
                                  points[pIdx] = { ...points[pIdx], tier: Number(e.target.value) };
                                  updateQuestion(question.id, { rubric: { ...(question.rubric || {}), points } });
                                }}
                                className="px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                              >
                                {RUBRIC_TIER_OPTIONS.map((t) => (
                                  <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => {
                                  const points = (question.rubric?.points || []).filter((_, i) => i !== pIdx);
                                  updateQuestion(question.id, { rubric: { ...(question.rubric || {}), points } });
                                }}
                                className="p-1 text-gray-400 hover:text-red-500"
                                aria-label="Remove rubric point"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          const points = [...(question.rubric?.points || []), { text: '', tier: DEFAULT_RUBRIC_POINT_TIER }];
                          updateQuestion(question.id, { rubric: { ...(question.rubric || {}), points } });
                        }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add rubric point
                      </button>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 min-h-[2.5rem]">
                      {question.rubric?.information_goal || question.rubric?.points?.length ? (
                        <div className="space-y-2">
                          {question.rubric?.information_goal && (
                            <p className="text-sm text-gray-900">{question.rubric.information_goal}</p>
                          )}
                          {question.rubric?.points && question.rubric.points.length > 0 && (
                            <ul className="space-y-1">
                              {question.rubric.points.map((point, pIdx) => (
                                <li key={pIdx} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="mt-0.5 shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600">
                                    {tierLabel(point.tier)}
                                  </span>
                                  <span>{point.text}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No rubric yet — one will be auto-derived when you save.</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Conditional Display Settings */}
              {index > 0 && (
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Display Condition
                    </label>
                    {editingQuestionId === question.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`enable-condition-${question.id}`}
                          checked={!!question.display_condition}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const eligibleQuestions = questions.slice(0, index).filter(
                                q => DISPLAY_CONDITION_TYPES.includes(q.type)
                              );
                              if (eligibleQuestions.length > 0) {
                                const sourceQ = eligibleQuestions[eligibleQuestions.length - 1];
                                const initialCond = sourceQ.type === 'percentage_allocation'
                                  ? { source_question_id: sourceQ.id, operator: '>' as const, option_key: sourceQ.choices?.[0] || '', threshold: 0, value: '' }
                                  : { source_question_id: sourceQ.id, operator: 'selected' as const, value: sourceQ.choices?.[0] || '' };
                                updateQuestion(question.id, {
                                  display_condition: { logic: 'and', conditions: [initialCond] }
                                });
                              }
                            } else {
                              updateQuestion(question.id, { display_condition: undefined });
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`enable-condition-${question.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Add display condition
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        question.display_condition
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {question.display_condition ? 'Conditional' : 'Disabled'}
                      </span>
                    )}
                  </div>

                  {/* Condition Configuration - supports compound AND/OR conditions */}
                  {editingQuestionId === question.id && question.display_condition && (() => {
                    const dc = question.display_condition;
                    const conditions = dc.conditions || (dc.source_question_id ? [{ source_question_id: dc.source_question_id, operator: dc.operator || 'selected', value: dc.value || '', option_key: dc.option_key, threshold: dc.threshold }] : []);
                    const logic = dc.logic || 'and';
                    const eligibleQuestions = questions.slice(0, index).filter(q => DISPLAY_CONDITION_TYPES.includes(q.type));

                    const updateConditions = (newConditions: any[], newLogic?: string) => {
                      updateQuestion(question.id, {
                        display_condition: { logic: (newLogic || logic) as 'and' | 'or', conditions: newConditions }
                      });
                    };

                    const renderConditionRow = (cond: any, condIdx: number) => {
                      const sourceQ = questions.find(q => q.id === cond.source_question_id);
                      const isPct = sourceQ?.type === 'percentage_allocation';
                      return (
                        <div key={condIdx} className="bg-white border border-gray-200 rounded p-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Condition {condIdx + 1}</span>
                            {conditions.length > 1 && (
                              <button type="button" onClick={() => updateConditions(conditions.filter((_: any, i: number) => i !== condIdx))} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                            )}
                          </div>
                          <select value={cond.source_question_id} onChange={(e) => {
                            const sq = questions.find(q => q.id === e.target.value);
                            const next = [...conditions];
                            next[condIdx] = sq?.type === 'percentage_allocation'
                              ? { source_question_id: e.target.value, operator: '>', option_key: sq.choices?.[0] || '', threshold: 0, value: '' }
                              : { source_question_id: e.target.value, operator: 'selected', value: sq?.choices?.[0] || '' };
                            updateConditions(next);
                          }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                            {eligibleQuestions.map(q => (
                              <option key={q.id} value={q.id}>Q{questions.indexOf(q) + 1}: {q.text.substring(0, 40)}{q.text.length > 40 ? '...' : ''}</option>
                            ))}
                          </select>
                          {isPct ? (
                            <div className="grid grid-cols-3 gap-1">
                              <select value={cond.option_key || ''} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, option_key: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                {(sourceQ?.choices || []).map((c: string, i: number) => <option key={i} value={c}>{c}</option>)}
                              </select>
                              <select value={cond.operator || '>'} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, operator: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                <option value=">">&gt;</option><option value=">=">&ge;</option><option value="=">=</option><option value="<">&lt;</option><option value="<=">&le;</option>
                              </select>
                              <input type="number" min="0" value={cond.threshold ?? 0} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, threshold: parseInt(e.target.value) || 0 }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs" />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-1">
                              <select value={cond.operator || 'selected'} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, operator: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                <option value="selected">selected</option><option value="not_selected">not selected</option>
                              </select>
                              <select value={cond.value || ''} onChange={(e) => { const next = [...conditions]; next[condIdx] = { ...cond, value: e.target.value }; updateConditions(next); }} className="px-1 py-1 border border-gray-300 rounded text-xs">
                                {(sourceQ?.choices || []).map((c: string, i: number) => <option key={i} value={c}>{c}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3">
                        <p className="text-xs text-gray-600">Only show this question if:</p>
                        {conditions.length > 1 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">Match:</span>
                            <select value={logic} onChange={(e) => updateConditions(conditions, e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-xs font-medium">
                              <option value="and">ALL conditions (AND)</option>
                              <option value="or">ANY condition (OR)</option>
                            </select>
                          </div>
                        )}
                        <div className="space-y-2">
                          {conditions.map((cond: any, condIdx: number) => (
                            <React.Fragment key={condIdx}>
                              {condIdx > 0 && (
                                <div className="text-center text-xs font-medium text-gray-400 uppercase">{logic}</div>
                              )}
                              {renderConditionRow(cond, condIdx)}
                            </React.Fragment>
                          ))}
                        </div>
                        {eligibleQuestions.length > 0 && (
                          <button type="button" onClick={() => {
                            const sq = eligibleQuestions[eligibleQuestions.length - 1];
                            const newCond = sq.type === 'percentage_allocation'
                              ? { source_question_id: sq.id, operator: '>', option_key: sq.choices?.[0] || '', threshold: 0, value: '' }
                              : { source_question_id: sq.id, operator: 'selected', value: sq.choices?.[0] || '' };
                            updateConditions([...conditions, newCond]);
                          }} className="text-xs text-blue-600 hover:text-blue-800">+ Add condition</button>
                        )}
                      </div>
                    );
                  })()}

                  {/* Show condition summary when not editing */}
                  {editingQuestionId !== question.id && question.display_condition && (
                    <div className="text-sm text-gray-600">
                      {(() => {
                        const dc = question.display_condition;
                        const conditions = dc.conditions || (dc.source_question_id ? [{ source_question_id: dc.source_question_id, operator: dc.operator, value: dc.value, option_key: dc.option_key, threshold: dc.threshold }] : []);
                        const logic = dc.logic || 'and';

                        const summaries = conditions.map((cond: any) => {
                          const sourceQ = questions.find(q => q.id === cond.source_question_id);
                          const sourceIdx = sourceQ ? questions.indexOf(sourceQ) + 1 : '?';
                          if (sourceQ?.type === 'percentage_allocation') {
                            const opDisplay: Record<string, string> = { '>': '>', '>=': '\u2265', '=': '=', '<': '<', '<=': '\u2264' };
                            return `Q${sourceIdx} "${cond.option_key}" ${opDisplay[cond.operator] || cond.operator} ${cond.threshold}`;
                          }
                          return `Q${sourceIdx} ${cond.operator === 'selected' ? '=' : '\u2260'} "${cond.value}"`;
                        });

                        const joiner = ` ${logic.toUpperCase()} `;
                        return `Show if ${summaries.join(joiner)}`;
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
            </React.Fragment>
  );
};

export default QuestionCard;
